import { AnalyticsListener } from "@atlaskit/analytics-next";
import { WacFreeBundlesView } from "@atlassiansox/bxpkit-wac-free-bundles";
import React from "react";
import { IntlProvider } from "react-intl";
import Environment from "../../../common/environment";
import tracking from '../../../utils/tracking';

const sendAnalytics = analytic => {
  const eventName =
    analytic.payload && analytic.payload.name
      ? analytic.payload.name
      : "wac-free-bundles-track-event";

  let eventPayload = analytic.payload;
  if (analytic.context[0]) {
    eventPayload = Object.assign(analytic.context[0], analytic.payload);
  }

  if (eventPayload.name !== undefined) {
    delete eventPayload.name;
  }

  try {
    tracking.trackEvent(eventName, eventPayload);
  } catch (e) {
    console.log(eventName, "AnalyticsListener did not find tracker");
  }
};

const FREE_PRODUCT_KEYS = {
  CONFLUENCE: 'confluence.ondemand',
  JIRA_SOFTWARE: 'jira-software.ondemand',
  JIRA_CORE: 'jira-core.ondemand',
  JIRA_SERVICE_DESK: 'jira-servicedesk.ondemand',
};

const getPrimaryProductKey = (productKey: string) => {
  switch (productKey) {
    case "jira-software":
      return FREE_PRODUCT_KEYS.JIRA_SOFTWARE;
    case "confluence":
      return FREE_PRODUCT_KEYS.CONFLUENCE;
    case "jira-service-management":
      return FREE_PRODUCT_KEYS.JIRA_SERVICE_DESK;
    case "jira-work-management":
      return FREE_PRODUCT_KEYS.JIRA_CORE;
    default:
      return "";
  }
};
/* Any combination of productKey & bundleRank that doesn't match
we are returning default static free bundle products  as that's expected behaviour */

const getSecondaryProductKeys = (
  productKey: string,
  freeBundleRank: string
): string[] => {
  switch (productKey + "&" + freeBundleRank) {
    case "jira-software&2":
      return [FREE_PRODUCT_KEYS.CONFLUENCE];
    case "jira-software&4":
      return [FREE_PRODUCT_KEYS.JIRA_SERVICE_DESK];
    case "confluence&2":
      return [FREE_PRODUCT_KEYS.JIRA_SOFTWARE];
    case "confluence&3":
      return [FREE_PRODUCT_KEYS.JIRA_SERVICE_DESK];
    case "jira-service-management&3":
      return [FREE_PRODUCT_KEYS.JIRA_SOFTWARE];
    case "jira-service-management&4":
      return [FREE_PRODUCT_KEYS.JIRA_SOFTWARE];
    default:
      return getDefaultSecondaryProducts(productKey);
  }
};

const getDefaultSecondaryProducts = (
  productKey: string
): string[] => {
  switch (productKey) {
    case "jira-software":
      return [FREE_PRODUCT_KEYS.CONFLUENCE, FREE_PRODUCT_KEYS.JIRA_SERVICE_DESK];
    case "confluence":
      return [FREE_PRODUCT_KEYS.JIRA_SOFTWARE, FREE_PRODUCT_KEYS.JIRA_SERVICE_DESK];
    case "jira-service-management":
      return [FREE_PRODUCT_KEYS.JIRA_SOFTWARE];
    case "jira-work-management":
      return [FREE_PRODUCT_KEYS.CONFLUENCE];
    default:
      return [];
  }
};

export type BundleMapType = { [index: string]: string };

export type ProductData = {
  readonly productKey: string;
  readonly productName: string;
  readonly productLogoUrl?: string;
  readonly productIconUrl?: string;
};

export type WacFreeBundlesContainerProps = {
  readonly primaryProductKey: string;
  readonly secondaryProductKeys: string[];
  readonly signUpPath: string;
  readonly bundleMap: BundleMapType;
  readonly comparePlansUrl: string;
  readonly productData?: { [index: string]: ProductData };
  readonly labels: any;
  readonly freeBundleRank: string;
  readonly primaryProductNode: string;
  readonly isExpressCheckout: boolean;
  readonly featureFlags: featureFlags;
};
export type featureFlags = {
  readonly enableBundlesAfterSignup: boolean;
};

export type WacFreeBundlesRenderProps = {
  readonly signUpPath: string;
  readonly bundleMap: BundleMapType;
  readonly comparePlansUrl: string;
  readonly productData?: { [index: string]: ProductData };
  readonly labels: any;
  readonly freeBundleRank: string;
  readonly primaryProductNode: string;
  readonly featureFlags: featureFlags;
};


const WacFreeBundlesContainer = ({
  isExpressCheckout,
  signUpPath,
  primaryProductNode,
  freeBundleRank,
  bundleMap,
  comparePlansUrl,
  labels,
  productData,
  featureFlags
}: WacFreeBundlesContainerProps) => {
  /* we are changing to 'jira-service-management' as this will be used as a parameter in signup URL*/
  if (primaryProductNode === 'jira-service-desk') {
    primaryProductNode = 'jira-service-management';
  }

  if (primaryProductNode === 'jira-core') {
    primaryProductNode = 'jira-work-management';
  }

  if (isExpressCheckout) {
    window.location.href = `${Environment.getWacIntegrationUrl()}${signUpPath}?bundle=${primaryProductNode}&edition=free&express=true`;
    return;
  } else {
    return WacFreeBundlesRender({
      primaryProductNode,
      freeBundleRank,
      signUpPath,
      bundleMap,
      comparePlansUrl,
      labels,
      productData,
      featureFlags
    });
  }
};

const WacFreeBundlesRender = ({
  primaryProductNode,
  freeBundleRank,
  signUpPath,
  bundleMap,
  comparePlansUrl,
  labels,
  productData,
  featureFlags
}: WacFreeBundlesRenderProps) => {
  const primaryProductKey = getPrimaryProductKey(primaryProductNode);
  const secondaryProductKeys = getSecondaryProductKeys(
    primaryProductNode,
    freeBundleRank
  );

  return (
    <div>
      <AnalyticsListener onEvent={sendAnalytics}>
        <IntlProvider
          locale={document.documentElement.lang}
          messages={labels}
        >
          <WacFreeBundlesView
            primaryProductKey={primaryProductKey}
            secondaryProductKeys={secondaryProductKeys}
            signUpPath={signUpPath}
            bundleMap={bundleMap}
            comparePlansUrl={comparePlansUrl}
            productData={productData}
            featureFlags={featureFlags}
          />
        </IntlProvider>
      </AnalyticsListener>
    </div>
  );
};

export default WacFreeBundlesContainer;
